import { useEffect } from 'react';

export interface OrganizationSchema {
  '@type': 'Organization';
  name: string;
  url: string;
  logo?: string;
  description?: string;
  address?: {
    '@type': 'PostalAddress';
    addressCountry?: string;
  };
  sameAs?: string[];
}

export interface WebPageSchema {
  '@type': 'WebPage';
  name: string;
  url: string;
  description: string;
  publisher?: OrganizationSchema;
}

export interface BlogPostingSchema {
  '@type': 'BlogPosting';
  headline: string;
  description?: string;
  url?: string;
  datePublished?: string;
  dateModified?: string;
  author?: OrganizationSchema;
  publisher?: OrganizationSchema;
}

export interface FAQPageSchema {
  '@type': 'FAQPage';
  mainEntity: Array<{
    '@type': 'Question';
    name: string;
    acceptedAnswer: {
      '@type': 'Answer';
      text: string;
    };
  }>;
}

type SchemaType = OrganizationSchema | WebPageSchema | BlogPostingSchema | FAQPageSchema | Record<string, any>;

interface StructuredDataProps {
  data: SchemaType;
}

export const StructuredData = ({ data }: StructuredDataProps) => {
  useEffect(() => {
    const structuredData = {
      '@context': 'https://schema.org',
      ...data
    };

    // Create or update the script tag
    const existingScript = document.querySelector('script[type="application/ld+json"][data-schema="dynamic"]');
    
    if (existingScript) {
      existingScript.textContent = JSON.stringify(structuredData);
    } else {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'dynamic');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"][data-schema="dynamic"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [data]);

  return null;
};
